import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";
import StatusBadge from "@/components/StatusBadge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay,
} from "@dnd-kit/core";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LayoutGrid, List, GripVertical, CalendarClock, Share2, Mail } from "lucide-react";

const STATUSES = ["Open", "In Progress", "Blocked", "Completed", "Deferred", "Cancelled"];
const BOARD_COLS = ["Open", "In Progress", "Blocked", "Completed"];
const COL_ACCENT = {
  Open: "hsl(var(--muted-foreground))", "In Progress": "hsl(var(--chart-2))",
  Blocked: "hsl(var(--destructive))", Completed: "hsl(var(--ink-success))",
};
const PRIO_STYLE = {
  Urgent: "tone-blush", High: "tone-apricot", Critical: "tone-blush",
  Medium: "tone-powder", Low: "tone-lavender",
};

function isOverdue(t) {
  if (!t.due_date || ["Completed", "Cancelled", "Deferred"].includes(t.status)) return false;
  return new Date(t.due_date) < new Date(new Date().toDateString());
}

function KanbanCard({ t, overlay, onShare }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: t.id });
  const over = isOverdue(t);
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-card-${t.id}`}
      {...attributes}
      {...listeners}
      style={{ opacity: isDragging && !overlay ? 0.35 : 1 }}
      className="group relative rounded-2xl bg-card border border-border p-3.5 shadow-card hover:shadow-lift hover:border-[hsl(var(--cyan)/.45)] transition-[box-shadow,border-color] cursor-grab active:cursor-grabbing"
    >
      {!overlay && onShare && (
        <button
          data-testid={`task-share-card-${t.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onShare(t); }}
          title="Share with a client"
          className="absolute top-2 right-2 z-10 p-1 rounded-md text-muted-foreground/60 hover:text-primary hover:bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Share2 className="w-3.5 h-3.5" />
        </button>
      )}
      <div className="flex items-start gap-2">
        <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm leading-snug pr-5">{t.title}</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
            <span>{t.assigned_to_name || "Unassigned"}</span>
            {t.due_date && (
              <span className={`inline-flex items-center gap-0.5 ${over ? "font-semibold text-[hsl(var(--ink-danger))]" : ""}`}>
                <CalendarClock className="w-3 h-3" />{t.due_date}
              </span>
            )}
            {t.priority && <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ${PRIO_STYLE[t.priority] || PRIO_STYLE.Medium}`}>{t.priority}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Column({ status, items, onShare }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-column-${status.replace(/\s+/g, "-").toLowerCase()}`}
      className={`rounded-3xl border p-3.5 transition-colors ${isOver ? "border-primary bg-primary/5" : "border-border bg-secondary/40"}`}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em]">
          <span className="w-2 h-2 rounded-full" style={{ background: COL_ACCENT[status] }} />{status}
        </span>
        <span className="text-xs text-muted-foreground">{items.length}</span>
      </div>
      <div className="space-y-2 min-h-[60px]">
        {(items || []).map((t) => <KanbanCard key={t.id} t={t} onShare={onShare} />)}
      </div>
    </div>
  );
}

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("All");
  const [view, setView] = useState("board");
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [shareTask, setShareTask] = useState(null);
  const [shareEmail, setShareEmail] = useState("");
  const [sharing, setSharing] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = () =>
    api.get("/tasks").then(({ data }) => setTasks(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  function openShare(t) { setShareTask(t); setShareEmail(""); }

  async function shareWithGuest() {
    const email = shareEmail.trim();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSharing(true);
    try {
      const { data } = await api.post(`/ac/tasks/${shareTask.id}/share`, { email });
      if (data.personal_inbox) {
        toast.warning(`Sent to ${email} — note this looks like a personal inbox. Anyone with the link can mark it done.`);
      } else {
        toast.success(`Task shared with ${email}`);
      }
      setShareTask(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSharing(false); }
  }

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/tasks/${id}`, { status });
      toast.success("Task updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  function onDragEnd(event) {
    setDragId(null);
    const { active, over } = event;
    if (!over) return;
    const task = (tasks || []).find((t) => t.id === active.id);
    if (!task || task.status === over.id) return;
    // optimistic move
    setTasks((prev) => (prev || []).map((t) => (t.id === active.id ? { ...t, status: over.id } : t)));
    updateStatus(active.id, over.id);
  }

  const visible = filter === "All" ? tasks : (tasks || []).filter((t) => t.status === filter);
  const activeTask = (tasks || []).find((t) => t.id === dragId);

  return (
    <div data-testid={TEST_IDS.tasksRoot} className="p-6 md:p-8 lg:p-12">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="eyebrow">Action items</div>
          <h1 className="font-heading text-4xl sm:text-5xl mt-3 leading-none">Tasks.</h1>
        </div>
        <div className="flex items-center gap-2 bg-card border border-border rounded-full p-1 shadow-card">
          <button data-testid="tasks-view-board" onClick={() => setView("board")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] ${view === "board" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            <LayoutGrid className="w-3.5 h-3.5" />Board
          </button>
          <button data-testid="tasks-view-list" onClick={() => setView("list")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            <List className="w-3.5 h-3.5" />List
          </button>
        </div>
      </header>

      {view === "board" ? (
        loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <DndContext sensors={sensors} onDragStart={(e) => setDragId(e.active.id)} onDragEnd={onDragEnd} onDragCancel={() => setDragId(null)}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kanban-board">
              {BOARD_COLS.map((col) => (
                <Column key={col} status={col} items={(tasks || []).filter((t) => t.status === col)} onShare={openShare} />
              ))}
            </div>
            <DragOverlay>{activeTask ? <KanbanCard t={activeTask} overlay /> : null}</DragOverlay>
          </DndContext>
        )
      ) : (
        <>
          <div className="flex gap-2 flex-wrap mb-5">
            {["All", ...STATUSES].map((s) => (
              <button key={s} onClick={() => setFilter(s)}
                data-testid={`task-filter-${s.toLowerCase().replace(/\s/g, "-")}`}
                className={"rounded-full px-3.5 py-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold transition-colors " +
                  (filter === s ? "bg-primary text-primary-foreground shadow-lift" : "bg-card border border-border text-foreground/80 hover:bg-secondary")}>
                {s}
              </button>
            ))}
          </div>
          <div className="yl-surface overflow-x-auto yl-scroll">
            <table className="w-full text-sm min-w-[920px]">
              <thead className="bg-secondary/60">
                <tr className="text-left">
                  <th className="px-5 py-3 eyebrow font-bold">Task</th>
                  <th className="px-5 py-3 eyebrow font-bold">Owner</th>
                  <th className="px-5 py-3 eyebrow font-bold">Due</th>
                  <th className="px-5 py-3 eyebrow font-bold">Priority</th>
                  <th className="px-5 py-3 eyebrow font-bold">Status</th>
                  <th className="px-5 py-3 eyebrow font-bold">Meeting</th>
                  <th className="px-5 py-3 eyebrow font-bold w-16 text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
                {!loading && visible.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">No tasks here.</td></tr>}
                {(visible || []).map((t) => (
                  <tr key={t.id} data-testid={TEST_IDS.taskRow} className="border-t border-border hover:bg-secondary/40 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-semibold">{t.title}</div>
                      {t.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2 max-w-md">{t.description}</div>}
                    </td>
                    <td className="px-5 py-4 text-sm">{t.assigned_to_name || "Unassigned"}</td>
                    <td className="px-5 py-4 text-sm">{t.due_date || "—"}</td>
                    <td className="px-5 py-4"><StatusBadge value={t.priority} /></td>
                    <td className="px-5 py-4">
                      <Select data-testid={`task-status-select-${t.id}`} value={t.status} onValueChange={(v) => updateStatus(t.id, v)}>
                        <SelectTrigger data-testid={TEST_IDS.taskStatusSelect} className="rounded-full h-8 w-[140px] border-border bg-background"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          {STATUSES.map((s) => <SelectItem key={s} data-testid={`task-status-opt-${s}`} value={s} className="rounded-xl">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-4 text-sm">
                      {t.meeting_id ? (
                        <Link to={`/meetings/${t.meeting_id}`} data-testid={`task-meeting-link-${t.id}`} className="text-primary hover:underline underline-offset-4">{t.meeting_title || "Open"}</Link>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button data-testid={`task-share-row-${t.id}`} onClick={() => openShare(t)} title="Share with a client"
                        className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                        <Share2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={!!shareTask} onOpenChange={(o) => !o && setShareTask(null)}>
        <DialogContent data-testid="task-share-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Share2 className="w-4 h-4" /> Share with a client</DialogTitle>
            <DialogDescription className="text-xs">
              Email anyone — no MeetIQ account needed. They can mark <b>{shareTask?.title}</b> as done straight from their inbox.
            </DialogDescription>
          </DialogHeader>
          <Input data-testid="task-share-input" type="email" value={shareEmail}
            onChange={(e) => setShareEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && shareWithGuest()}
            placeholder="client@company.com" className="h-10" />
          <DialogFooter>
            <Button data-testid="task-share-send-btn" disabled={sharing} onClick={shareWithGuest}>
              <Mail className="w-4 h-4 mr-1.5" />{sharing ? "Sending…" : "Send link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
